/**
 * "Find my mattress" quiz (snippets/omi-mattress-finder.liquid).
 *
 * Firmness follows sleep position (side: Medium, back and combination: Medium-Firm, stomach: Firm),
 * one step firmer for sleepers over 250 lb. Two sleepers who land on different firmness are pointed
 * to a layer-builder mattress (the only kind offered with a different firmness on each side), whose layers can be split by side. Recommendations come from the
 * collection data in the page; nothing is stored.
 */
const SCALE = ['Plush', 'Medium', 'Medium-Firm', 'Firm'];
const POSITIONS = [
  { value: 'side', label: 'On my side', note: 'Hips and shoulders need room to sink in' },
  { value: 'back', label: 'On my back', note: 'The spine needs even support' },
  { value: 'stomach', label: 'On my stomach', note: 'Hips need to stay level' },
  { value: 'combination', label: 'A bit of everything', note: 'I change position through the night' },
];
const BY_POSITION = { side: 'Medium', back: 'Medium-Firm', stomach: 'Firm', combination: 'Medium-Firm' };

/** @param {string} s */
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);
/** @param {number} cents */
const money = (cents) => '$' + (cents / 100).toLocaleString('en-US', { maximumFractionDigits: cents % 100 ? 2 : 0 });

class OmiMattressFinder extends HTMLElement {
  /** @type {any[]} */ products = [];
  /** @type {Record<string, string>} */ answers = {};
  step = 0;

  connectedCallback() {
    try {
      this.products = JSON.parse(this.querySelector('script[data-omi-finder-products]')?.textContent || '[]');
    } catch {
      this.products = [];
    }
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('dialog'));
    this.body = /** @type {HTMLElement} */ (this.querySelector('[data-omi-finder-body]'));
    this.dialog.addEventListener('click', this.#onClick);
    this.dialog.addEventListener('close', () => this.opener?.focus?.());
    document.addEventListener('click', (event) => {
      const trigger = /** @type {HTMLElement} */ (event.target).closest('a[href="#find-my-mattress"], [data-omi-finder]');
      if (!trigger) return;
      event.preventDefault();
      this.open();
    });
    if (window.location.hash === '#find-my-mattress') this.open();
  }

  open() {
    this.answers = {};
    this.step = 0;
    this.opener = /** @type {HTMLElement | null} */ (document.activeElement);
    this.#render();
    if (!this.dialog?.open) this.dialog?.showModal();
  }

  get steps() {
    const two = this.answers.sleepers === 'two';
    /** @type {{key: string, title: string, options: {value: string, label: string, note?: string}[]}[]} */
    const list = [
      { key: 'sleepers', title: 'Who sleeps on the mattress?', options: [{ value: 'one', label: 'Just me' }, { value: 'two', label: 'Two of us' }] },
      { key: 'posA', title: two ? 'How do you sleep?' : 'How do you usually sleep?', options: POSITIONS },
      { key: 'heavyA', title: two ? 'Are you over 250 lb?' : 'Are you over 250 lb?', options: [{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes', note: 'Firmer support keeps you from sinking too far' }] },
    ];
    if (two) {
      list.push(
        { key: 'posB', title: 'How does your partner sleep?', options: POSITIONS },
        { key: 'heavyB', title: 'Is your partner over 250 lb?', options: [{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }] }
      );
    }
    list.push({
      key: 'build',
      title: 'Any preference on how it is built?',
      options: [
        { value: 'All-Latex', label: 'All latex', note: 'Buoyant and quiet, with no springs' },
        { value: 'Hybrid (Latex Over Coils)', label: 'Latex over coils', note: 'A springier feel with more airflow' },
        { value: 'any', label: 'No preference' },
      ],
    });
    return list;
  }

  /** @param {string} pos @param {string} heavy */
  firmness(pos, heavy) {
    const base = BY_POSITION[/** @type {keyof typeof BY_POSITION} */ (pos)] || 'Medium-Firm';
    const i = SCALE.indexOf(base);
    return heavy === 'yes' ? SCALE[Math.min(SCALE.length - 1, i + 1)] : base;
  }

  /** @param {any} p */
  buildOk(p) {
    return this.answers.build === 'any' || !this.answers.build || p.build === this.answers.build;
  }

  results() {
    const a = this.answers;
    const fA = this.firmness(a.posA, a.heavyA);
    const fB = a.sleepers === 'two' ? this.firmness(a.posB, a.heavyB) : fA;
    const split = fA !== fB;
    const norm = (/** @type {string} */ s) => String(s || '').toLowerCase();
    /** Firmness levels a mattress comes in: its list of levels when it has one, else its single firmness */
    const levels = (/** @type {any} */ p) => (Array.isArray(p.levels) && p.levels.length ? p.levels : [p.firmness]).map(norm);

    /** @type {any[]} */ let picks = [];
    if (split) {
      // Only layer-builder mattresses (the Duo) are offered with a different firmness on each side
      const layered = this.products.filter((p) => p.layers);
      picks = [...layered.filter((p) => this.buildOk(p)), ...layered];
    } else {
      const exact = this.products.filter((p) => levels(p).includes(norm(fA)));
      const i = SCALE.indexOf(fA);
      const near = this.products.filter((p) => [SCALE[i - 1], SCALE[i + 1]].filter(Boolean).map(norm).some((f) => levels(p).includes(f)));
      const custom = this.products.filter((p) => !(Array.isArray(p.levels) && p.levels.length) && norm(p.firmness) === 'customizable');
      picks = [...exact.filter((p) => this.buildOk(p)), ...custom.filter((p) => this.buildOk(p)), ...near.filter((p) => this.buildOk(p)), ...exact, ...near];
    }
    picks = picks.filter((p, i, all) => all.indexOf(p) === i).slice(0, 3);

    let why = '';
    if (split) why = `You need ${fA.toLowerCase()} and your partner ${fB.toLowerCase()}. ${picks.length === 1 ? `Each side of the ${picks[0].title} can be built to its own firmness.` : 'Each side of these mattresses can be built to its own firmness.'}`;
    else if (a.heavyA === 'yes' || a.heavyB === 'yes') why = `${fA} gives the extra support heavier sleepers need, without feeling hard.`;
    else if (a.posA === 'side') why = 'Side sleepers need enough give at the hips and shoulders. Medium balances comfort and support.';
    else if (a.posA === 'back' || a.posA === 'combination') why = 'Medium-firm keeps the spine aligned without feeling rigid, whichever way you turn.';
    else if (a.posA === 'stomach') why = 'Stomach sleepers need a firm surface so the hips stay level with the shoulders.';
    return { fA, fB, split, picks, why };
  }

  #render() {
    const steps = this.steps;
    const done = this.step >= steps.length;
    const bar = /** @type {HTMLElement} */ (this.querySelector('[data-omi-finder-bar]'));
    bar.style.width = `${Math.round((Math.min(this.step, steps.length) / steps.length) * 100)}%`;
    const back = /** @type {HTMLButtonElement} */ (this.querySelector('[data-omi-finder-back]'));
    const next = /** @type {HTMLButtonElement} */ (this.querySelector('[data-omi-finder-next]'));
    back.textContent = this.step === 0 ? 'Cancel' : 'Back';
    next.hidden = done;

    if (!done) {
      const s = steps[this.step];
      next.disabled = !this.answers[s.key];
      next.textContent = this.step === steps.length - 1 ? 'See my matches' : 'Continue';
      this.body.innerHTML = `
        <p class="omi-finder__count">Question ${this.step + 1} of ${steps.length}</p>
        <h2 class="h3 omi-finder__title" id="OmiFinderTitle" tabindex="-1">${esc(s.title)}</h2>
        <div class="omi-finder__options" role="group" aria-labelledby="OmiFinderTitle">
          ${s.options
            .map(
              (o) => `<button type="button" class="omi-finder__option" data-key="${s.key}" data-value="${esc(o.value)}" aria-pressed="${this.answers[s.key] === o.value}">
                <span class="omi-finder__option-label">${esc(o.label)}</span>${o.note ? `<span class="omi-finder__option-note">${esc(o.note)}</span>` : ''}
              </button>`
            )
            .join('')}
        </div>
        <p class="omi-finder__privacy">We use your answers only to suggest a mattress. Nothing is saved.</p>`;
    } else {
      const r = this.results();
      const consult = this.dataset.consult;
      this.body.innerHTML = `
        <p class="omi-finder__count">Your matches</p>
        <h2 class="h3 omi-finder__title" id="OmiFinderTitle" tabindex="-1">${r.split ? `${esc(r.fA)} for you, ${esc(r.fB.toLowerCase())} for your partner` : esc(r.fA)}</h2>
        ${r.why ? `<p class="omi-finder__why">${esc(r.why)}</p>` : ''}
        <ul class="omi-finder__results list-unstyled">
          ${r.picks
            .map(
              (p, i) => `<li class="omi-finder__result${i === 0 ? ' is-lead' : ''}">
                <a href="${esc(p.url)}" class="omi-finder__result-link">
                  ${p.image ? `<img src="${esc(p.image)}" alt="" width="300" height="225" loading="lazy">` : ''}
                  <span class="omi-finder__result-text">
                    ${i === 0 ? '<span class="omi-finder__badge">Best match</span>' : ''}
                    <span class="omi-finder__result-line">${esc(p.line)}</span>
                    <span class="omi-finder__result-title">${esc(p.title)}</span>
                    <span class="omi-finder__result-meta">${esc([p.build?.split(' (')[0], (Array.isArray(p.levels) && p.levels.length ? p.levels.join(' / ') : p.firmness), p.profile ? `${p.profile}″` : ''].filter(Boolean).join(' · '))}</span>
                    <span class="omi-finder__result-price">From ${p.compare > p.price ? `<span class="price price-item--sale">${money(p.price)}</span> <s class="compare-at-price">${money(p.compare)}</s>` : money(p.price)}</span>
                    ${r.split && p.layers ? '<span class="omi-finder__result-note">Choose each side’s layers on the product page</span>' : ''}
                  </span>
                </a>
              </li>`
            )
            .join('')}
        </ul>
        <div class="omi-finder__after">
          ${consult ? `<a class="button-secondary" href="${esc(consult)}" data-omi-consult>Talk to a sleep specialist</a>` : ''}
          <button type="button" class="omi-finder__restart" data-omi-finder-restart>Start again</button>
        </div>`;
    }
    /** @type {HTMLElement | null} */ (this.body.querySelector('#OmiFinderTitle'))?.focus();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const t = /** @type {HTMLElement} */ (event.target);
    if (t === this.dialog || t.closest('[data-omi-finder-close]')) return this.dialog?.close();
    const option = /** @type {HTMLElement | null} */ (t.closest('.omi-finder__option'));
    if (option) {
      this.answers[/** @type {string} */ (option.dataset.key)] = /** @type {string} */ (option.dataset.value);
      if (option.dataset.key === 'sleepers' && option.dataset.value === 'one') {
        delete this.answers.posB;
        delete this.answers.heavyB;
      }
      for (const b of this.body.querySelectorAll('.omi-finder__option')) b.setAttribute('aria-pressed', String(b === option));
      /** @type {HTMLButtonElement} */ (this.querySelector('[data-omi-finder-next]')).disabled = false;
      return;
    }
    if (t.closest('[data-omi-finder-next]')) {
      this.step += 1;
      return this.#render();
    }
    if (t.closest('[data-omi-finder-back]')) {
      if (this.step === 0) return this.dialog?.close();
      this.step -= 1;
      return this.#render();
    }
    if (t.closest('[data-omi-finder-restart]')) {
      this.answers = {};
      this.step = 0;
      return this.#render();
    }
    if (t.closest('[data-omi-consult]')) this.dialog?.close();
  };
}

if (!customElements.get('omi-mattress-finder')) customElements.define('omi-mattress-finder', OmiMattressFinder);
